import {
  FeatureAppTrackingManager,
  getDefaultTrackingEvent,
} from '../hooks/use-tracking-manager';
import _ from 'lodash';
import { CategoryModels, Dealer, ModelConfig } from '../typing/model';
import { GlobalModelData } from '../store/local-storage';
import { Offer } from '../typing/offer';
import { getAppliedFilterList, getDealerData, getResultsFilterData, getConfigurationData} from './tagging-utils';

export interface LinkParams {
  name?: string;
  url?: string;
}

export interface Content {
  ItemCount?: number;
  ItemPosition?: number;
  CountdownTotalTime?: number;
  CountdownRemainingTime?: number;
  RSSFeedsCount?: number;
}

export interface Link {
  name?: string;
  url?: string;
}

const fieldsErrorCodes = {
  'FE01': 'Something went wrong',
  'INV01': 'Invalid Zip',
  'INV02': 'Communication Error',
  '': 'FE00',
};

/**
 * Simple example of creating a tagging event for a button click
 * @param contentData
 * @param linkData
 * @param trackingManager
 */
export const onVWButtonClickEvent = (
  contentData: Content,
  linkData: Link,
  trackingManager: FeatureAppTrackingManager,
) => {
  let customEvent = getDefaultTrackingEvent();

  // These objects properties required to be filled based on the data
  // that we are able to manipulate
  customEvent.environment = {
    ...customEvent.environment,
    commerce: true,
  };

  customEvent.eventInfo = {
    ...customEvent.eventInfo,
    contentId: 'Button click',
  };

  customEvent.content = {
    ...customEvent.content,
    ...contentData,
  };

  customEvent.user = {
    ...customEvent.user,
    loginStatus: false,
    customerType: undefined || '',
    customerContractsCount: undefined,
    dealerStatus: false,
  };

  customEvent.link = {
    ...linkData,
  };

  trackingManager &&
    trackingManager.trackFeatureAppGlobalEvent('VW_Button_Click', customEvent);
};

export const onHandlerDataLoaded = (
  trackingManager: FeatureAppTrackingManager,
  store: any,
) => {
  let customEvent = getDefaultTrackingEvent();
  customEvent.filter = getAppliedFilterList(store.offers, customEvent.filter);
  customEvent.filter = getResultsFilterData(store.offers, customEvent.filter);
  customEvent.partner = getDealerData(store.dealer, customEvent.partner);
  trackingManager &&
    trackingManager.trackFeatureAppGlobalEvent(
      'VWBasic_FilterSearchResult_Load',
      customEvent,
    );
};

export const onVWBasicErrorMessageLoad = (
  trackingManager: FeatureAppTrackingManager,
  modelsConfigVar: CategoryModels[],
  globalModelData: GlobalModelData | undefined,
  errorId: string,
) => {
  let customEvent = getDefaultTrackingEvent();

  customEvent.environment = {
    ...customEvent.environment,
    commerce: false,
  };

  customEvent.user = {
    loginStatus: false,
  };

  if (globalModelData && globalModelData.slug) {
    let curModelConfig: ModelConfig | undefined;

    _.find(modelsConfigVar, function (categories) {
      _.find(categories.models, function (modelConfig) {
        if (modelConfig.slug === globalModelData?.slug) {
          curModelConfig = modelConfig;
          return true;
        }
        return false;
      });
    });

    customEvent.configuration = {
      Manufacturer: 'Volkswagen',
      BodyTypeName: curModelConfig?.category, //TODO Get category like sedan, compact that comes from the modelsdata. Either make query or use a apollo variable
      CarlineId: curModelConfig?.carlineId,
      CarlineName: curModelConfig?.name,
      PriceTotalCurrency: 'USD',
      UsedVehicle: false,
    };
  }

  customEvent.form = {
    orderId: '1',
  };

  customEvent.process = {
    Name: 'Validate Zip',
    StepName: 'Validate Zip',
    StepNumber: '1',
    Components: ['Zip Entry'],
  };

  customEvent.error = {
    code: errorId,
    message: fieldsErrorCodes[errorId],
  };

  trackingManager &&
    trackingManager.trackFeatureAppGlobalEvent(
      'VWBasic_ErrorMessage_Load',
      customEvent,
    );
};

interface TrackingApplicationData {
  appStatus: 'loading' | 'error' | 'success' | 'initial' | 'empty';
  error?: boolean;
  dealer?: Dealer;
  offers?: Offer[];
  dealers?: Dealer[];
  offer?: Offer;
  modelConfig?: ModelConfig;
}

/**
 * handle no form vehicle link
 * @param linkRef
 * @param name
 * @param trackingManager
 * @param store
 */
export const onHandlerBasicVehicleLink = (
  linkRef?: string,
  name?: string,
  trackingManager?: FeatureAppTrackingManager,
  store?: TrackingApplicationData,
) => {
  let customEvent = getDefaultTrackingEvent();
  customEvent.configuration = {
    ...customEvent.configuration,
    CarlineName: store?.modelConfig?.name,
    CarlineId: store?.modelConfig?.carlineId
  };
  customEvent.link = {
    ...customEvent.link,
    url: linkRef,
    name: name
  };
  customEvent.partner = getDealerData(store?.dealer, customEvent.partner);
  trackingManager &&
    trackingManager.trackFeatureAppGlobalEvent(
      'VWBasic_VehicleLink_Click',
      customEvent
    );
};




/**
 * Handle form load
 * @param type
 * @param formType
 * @param formName
 * @param offer
 * @param trackingManager
 */
export const onHandleLayerLoadTracking = (
  type: 'load' | 'close',
  store?: TrackingApplicationData | any,
  trackingManager?: FeatureAppTrackingManager
) => {
  const event =
    type === 'load'
      ? 'VWBasic_Layer_Load'
      : 'VWBasic_LayerClose_Click';
  let customEvent = getDefaultTrackingEvent();
  if (type !== 'close') {
    customEvent.configuration = getConfigurationData(
      store?.offer,
store?.modelConfig,
      customEvent.configuration
    );
  }
  customEvent.eventInfo = {
    ...customEvent.eventInfo,
    contentId: "inventory-vehicle-offers-layer"
  };
  customEvent.link = {
    ...customEvent.link,
    url: type === 'close' ? 'close layer' : 'open layer',
    name: type === 'close' ? 'close offer details layer ' : 'open offer details layer'
  };
  customEvent.partner = getDealerData(store?.dealer, customEvent.partner);
  trackingManager &&
    trackingManager.trackFeatureAppGlobalEvent(event, customEvent);
};

/**
 * Handle form button link tag
 * @param offer
 * @param name
 * @param trackingManager
 * @param store
 * @param linkUrl
 */
export const onHandlePromotionButtonTracking = (
  name?: string,
  linkUrl?: string,
  store?: TrackingApplicationData,
  trackingManager?: FeatureAppTrackingManager,
) => {
  // Tracking section
  let customEvent = getDefaultTrackingEvent();
  customEvent.configuration = getConfigurationData(
    store?.offer,
    store?.modelConfig,
    customEvent.configuration
  );
  customEvent.partner = getDealerData(
    store?.dealer,
    customEvent.partner
  );
  customEvent.link = {
    ...customEvent.link,
    url: linkUrl || `${name} layer`,
    name: name
  };
  customEvent.eventInfo = {
    ...customEvent.eventInfo,
    contentId: linkUrl || `${name} form`
  };
  trackingManager &&
    trackingManager.trackFeatureAppGlobalEvent(
      'VWBasic_VehiclePromotionsButton_Click',
      customEvent
    );
};

/**
 * Filter accordion tracking handle
 * @param status
 * @param filterType
 * @param trackingManager
 * @param store
 */
export const onHandleBasicAccordionAction = (
  status: boolean,
  trackingManager: FeatureAppTrackingManager
) => {
  //tracking section
  let customEvent = getDefaultTrackingEvent();
  const filterType = "Available Offers";
  customEvent.link = {
    ...customEvent.link,
    url: status
      ? `Open Accordion ${filterType}`
      : `Close Accordion ${filterType}`,
    name: filterType
  };
  customEvent.filter = {
    ...customEvent.filter,
    name: filterType
  };

  customEvent.eventInfo = {
    ...customEvent.eventInfo,
    contentId: filterType,
    sectionName: 'Filters'
  };

  trackingManager.trackFeatureAppGlobalEvent(
    status ? 'VWBasic_AccordionOpen_Click' : 'VWBasic_AccordionClose_Click',
    customEvent
  );
};

/**
 * On Form Layer load event
 * @param name
 * @param trackingManager
 * @param store
 */
export const onVWBasicLinkClickEvent = (
  trackingManager: FeatureAppTrackingManager,
  contentId: string,
  link: { url: string; name: string; newTab?: boolean },
) => {
  let customEvent = getDefaultTrackingEvent();
  customEvent.eventInfo = { ...customEvent.eventInfo, contentId: contentId };
  customEvent.link = link;
  trackingManager &&
    trackingManager.trackFeatureAppGlobalEvent(
      'VWBasic_Link_Click',
      customEvent,
    );
};

/**
 * handle zipcode change tracking
 * @param trackingManager
 */
export const onVWBasicLayerLoadEvent = (
  trackingManager?: FeatureAppTrackingManager,
) => {
  let customEvent = getDefaultTrackingEvent();
  customEvent.eventInfo = { ...customEvent.eventInfo, contentId: 'ZIP Layer' };

  customEvent.user = {
    loginStatus: false,
  };

  trackingManager &&
    trackingManager.trackFeatureAppGlobalEvent(
      'VWBasic_Layer_Load',
      customEvent,
    );
};

/**
 * Handle vehicle telephone number click event
 * @param trackingManager
 * @param modelsConfigVar
 * @param vehicle
 * @param linkParams
 */
export const onVWBasicVehicleTelephoneNumberClick = (
  trackingManager: FeatureAppTrackingManager,
  dealer?: Dealer,
  linkParams?: LinkParams,
) => {
  let curModelConfig: ModelConfig | undefined;
  let customEvent = getDefaultTrackingEvent();

  customEvent.content = {
    Asset: undefined,
    ItemCount: undefined,
    ItemPosition: undefined,
    Name: undefined,
    Type: undefined,
  };

  customEvent.process = {
    StepName: undefined,
  };

  customEvent.link = {
    ...linkParams,
  };

  if (curModelConfig) {
    customEvent.environment = {
      ...customEvent.environment,
      region: dealer?.state,
    };

    customEvent.partner = {
      informationBnr: dealer?.dealerid,
      informationName: dealer?.name,
      informationZIP: dealer?.postalcode,
      informationDistance: dealer?.distance,
      informationDepartment: undefined,
    };
  }

  trackingManager &&
    trackingManager.trackFeatureAppGlobalEvent(
      'VWBasic_VehicleTelephoneNumber_Click',
      customEvent,
    );
};
